import React, { useCallback, useEffect, useRef, useState } from 'react';
import { grey } from '@ant-design/colors';
import { Button, Divider, message, Typography, Card, Tooltip } from 'antd';
import { useHistory } from 'react-router';
import { ApolloError } from '@apollo/client';
import styled from 'styled-components';
import { ChromePicker } from 'react-color';
import ColorHash from 'color-hash';
import { PlusOutlined } from '@ant-design/icons';
import { useGetTagQuery } from '../../graphql/tag.generated';
import { EntityType, FacetMetadata, Maybe, Scalars } from '../../types.generated';
import { ExpandedOwner } from '../entity/shared/components/styled/ExpandedOwner/ExpandedOwner';
import { EMPTY_MESSAGES } from '../entity/shared/constants';
import { navigateToSearchUrl } from '../search/utils/navigateToSearchUrl';
import { useEntityRegistry } from '../useEntityRegistry';
import { useUpdateDescriptionMutation, useSetTagColorMutation } from '../../graphql/mutations.generated';
import { useGetSearchResultsForMultipleQuery } from '../../graphql/search.generated';
import analytics, { EventType, EntityActionType } from '../analytics';
import { GetSearchResultsParams, SearchResultInterface } from '../entity/shared/components/styled/search/types';
import { EditOwnersModal } from '../entity/shared/containers/profile/sidebar/Ownership/EditOwnersModal';
import CopyUrn from './CopyUrn';
import EntityDropdown from '../entity/shared/EntityDropdown';
import { EntityMenuItems } from '../entity/shared/EntityDropdown/EntityDropdown';
import { ErrorSection } from './error/ErrorSection';
import { generateOrFilters } from '../search/utils/generateOrFilters';
import { ENTITY_FILTER_NAME, UnionType } from '../search/utils/constants';
import { useTranslation } from '../../i18n/useTranslation';
import { useRbac } from '../entity/shared/useRbac';
import { ENTITY_ACTIONS } from '../entity/shared/rbacConstantsService';

function useWrappedSearchResults(params: GetSearchResultsParams) {
    const { data, loading, error } = useGetSearchResultsForMultipleQuery(params);
    return { data: data?.searchAcrossEntities, loading, error };
}

type SearchResultsInterface = {
    /** The offset of the result set */
    start: Scalars['Int'];
    /** The number of entities included in the result set */
    count: Scalars['Int'];
    /** The total number of search results matching the query and filters */
    total: Scalars['Int'];
    /** The search result entities */
    searchResults: Array<SearchResultInterface>;
    /** Candidate facet aggregations used for search filtering */
    facets?: Maybe<Array<FacetMetadata>>;
};

const TitleLabel = styled(Typography.Text)`
    &&& {
        color: ${grey[2]};
        font-size: 14px;
        display: block;
        line-height: 20px;
        font-weight: 700;
    }
`;

const TitleText = styled(Typography.Text)`
    &&& {
        color: ${grey[10]};
        font-weight: 700;
        font-size: 20px;
        line-height: 28px;
        display: inline-block;
        margin: 0px 7px;
    }
`;

const ColorPicker = styled.div`
    position: relative;
    display: inline-block;
`;

const ColorPickerButton = styled.div`
    width: 16px;
    height: 16px;
    border: none;
    border-radius: 50%;
`;

const ColorPickerPopOver = styled.div`
    position: absolute;
    z-index: 100;
`;

const DescriptionLabel = styled(Typography.Text)`
    &&& {
        text-align: left;
        font-weight: bold;
        font-size: 14px;
        line-height: 28px;
        color: rgb(38, 38, 38);
    }
`;

export const EmptyValue = styled.div`
    &:after {
        content: 'None';
        color: #b7b7b7;
        font-style: italic;
        font-weight: 100;
    }
`;

const DetailsLayout = styled.div`
    display: flex;
    justify-content: space-between;
`;

const StatsBox = styled.div`
    width: 180px;
    justify-content: left;
`;

const StatsLabel = styled(Typography.Text)`
    &&& {
        color: ${grey[10]};
        font-size: 14px;
        font-weight: 700;
        line-height: 28px;
    }
`;

const StatsButton = styled(Button)`
    padding: 0px 0px;
    margin-top: 0px;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
`;

const EmptyStatsText = styled(Typography.Text)`
    font-size: 15px;
    font-style: italic;
`;

const OwnerButtonEmptyTitle = styled.span`
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    color: ${grey[10]};
`;

const OwnerButtonTitle = styled.span`
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: ${grey[10]};
`;

const OwnersSection = styled.div<{ $isFromDrawer?: boolean }>`
    ${(props) =>
        !props.$isFromDrawer &&
        `
        width: 100%;
        max-width: 600px;
    `}
`;

const OwnersContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
`;

const TagName = styled.div`
    display: flex;
    align-items: center;
    justify-content: left;
`;

const ActionButtons = styled.div`
    display: flex;
`;

const TagHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: top;
`;

const { Paragraph } = Typography;

type Props = {
    urn: string;
    isFromDrawer?: boolean;
    onDeleteEntity?: () => void;
    useGetSearchResults?: (params: GetSearchResultsParams) => {
        data: SearchResultsInterface | undefined | null;
        loading: boolean;
        error: ApolloError | undefined;
    };
};

const generateColor = new ColorHash({
    saturation: 0.9,
});

/**
 * Responsible for displaying metadata about a tag
 */
export default function TagStyleEntity({
    urn,
    isFromDrawer = false,
    onDeleteEntity,
    useGetSearchResults = useWrappedSearchResults,
}: Props) {
    const { t } = useTranslation();
    const history = useHistory();
    const entityRegistry = useEntityRegistry();
    const { canPerformEntityAction } = useRbac();
    const [setTagColorMutation] = useSetTagColorMutation();
    const [updateDescription] = useUpdateDescriptionMutation();
    const { error, data, refetch } = useGetTagQuery({ variables: { urn } });

    const canUpdateTagOwners = canPerformEntityAction(EntityType.Tag, ENTITY_ACTIONS.OWNERS);
    const canUpdateTagDescription = canPerformEntityAction(EntityType.Tag, ENTITY_ACTIONS.DOCS);
    const entityUrn = data?.tag?.urn;
    const entityFilters =
        (entityUrn && [
            {
                field: 'tags',
                values: [entityUrn],
            },
        ]) ||
        [];

    const description = data?.tag?.properties?.description || '';
    const [updatedDescription, setUpdatedDescription] = useState('');
    const hexColor = data?.tag?.properties?.colorHex || generateColor.hex(urn);
    const [displayColorPicker, setDisplayColorPicker] = useState(false);
    const [colorValue, setColorValue] = useState('');
    const ownersEmpty = !data?.tag?.ownership?.owners?.length;
    const [showAddModal, setShowAddModal] = useState(false);
    const [copiedUrn, setCopiedUrn] = useState(false);

    useEffect(() => {
        setUpdatedDescription(description);
    }, [description]);

    useEffect(() => {
        setColorValue(hexColor);
    }, [hexColor]);

    const { data: facetData, loading: facetLoading } = useGetSearchResults({
        variables: {
            input: {
                query: '*',
                start: 0,
                count: 1,
                orFilters: generateOrFilters(UnionType.OR, entityFilters),
            },
        },
    });

    const facets = facetData?.facets?.filter((facet) => facet?.field === 'entity') || [];
    const aggregations = facets && facets[0]?.aggregations;

    // Save Color Change
    const saveColor = useCallback(async () => {
        if (displayColorPicker) {
            try {
                await setTagColorMutation({
                    variables: {
                        urn,
                        colorHex: colorValue,
                    },
                });
                message.destroy();
                message.success({ content: t('Color Saved!'), duration: 2 });
                setDisplayColorPicker(false);
            } catch (e: unknown) {
                message.destroy();
                if (e instanceof Error) {
                    message.error({ content: `${t('Failed to save tag color:')} \n ${e.message || ''}`, duration: 2 });
                }
            }
            refetch?.();
        }
    }, [urn, colorValue, displayColorPicker, setTagColorMutation, setDisplayColorPicker, refetch, t]);

    const colorPickerRef = useRef(null);

    useEffect(() => {
        /**
         * Save Color if Clicked outside of the Color Picker
         */
        function handleClickOutsideColorPicker(event) {
            if (displayColorPicker) {
                const { current }: any = colorPickerRef;
                if (current) {
                    if (!current.contains(event.target)) {
                        setDisplayColorPicker(false);
                        saveColor();
                    }
                }
            }
        }
        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutsideColorPicker);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutsideColorPicker);
        };
    }, [colorPickerRef, displayColorPicker, saveColor]);

    const handlePickerClick = () => {
        setDisplayColorPicker(!displayColorPicker);
        saveColor();
    };

    const handleColorChange = (color: any) => {
        setColorValue(color?.hex);
    };

    // Update Description
    const updateDescriptionValue = (desc: string) => {
        setUpdatedDescription(desc);
        return updateDescription({
            variables: {
                input: {
                    description: desc,
                    resourceUrn: urn,
                },
            },
        });
    };

    // Save the description
    const handleSaveDescription = async (desc: string) => {
        message.loading({ content: t('Saving...') });
        try {
            await updateDescriptionValue(desc);
            message.destroy();
            message.success({ content: t('Description Updated'), duration: 2 });
            analytics.event({
                type: EventType.EntityActionEvent,
                actionType: EntityActionType.UpdateDescription,
                entityType: EntityType.Tag,
                entityUrn: urn,
            });
        } catch (e: unknown) {
            message.destroy();
            if (e instanceof Error) {
                message.error({ content: `${t('Failed to update description:')} \n ${e.message || ''}`, duration: 2 });
            }
        }
        refetch?.();
    };

    return (
        <>
            {error && <ErrorSection />}
            {/* Tag Title */}
            <Card className="pa-4 f-pa-4-xs">
                <TagHeader>
                    <div>
                        <TitleLabel>{t('Tag')}</TitleLabel>
                        <TagName>
                            <ColorPicker>
                                <ColorPickerButton
                                    style={{ backgroundColor: colorValue }}
                                    onClick={handlePickerClick}
                                />
                            </ColorPicker>
                            <TitleText>
                                {(data?.tag && entityRegistry.getDisplayName(EntityType.Tag, data?.tag)) || ''}
                            </TitleText>
                        </TagName>
                    </div>
                    <ActionButtons>
                        <CopyUrn
                            urn={urn}
                            isActive={copiedUrn}
                            tooltipPlacement="left"
                            onClick={() => setCopiedUrn(true)}
                        />
                        <EntityDropdown
                            urn={urn}
                            entityType={EntityType.Tag}
                            entityData={data?.tag}
                            menuItems={new Set([EntityMenuItems.COPY_URL, EntityMenuItems.DELETE])}
                            onDeleteEntity={onDeleteEntity}
                        />
                    </ActionButtons>
                    {displayColorPicker && (
                        <ColorPickerPopOver ref={colorPickerRef}>
                            <ChromePicker color={colorValue} onChange={handleColorChange} />
                        </ColorPickerPopOver>
                    )}
                </TagHeader>
            </Card>
            <Divider />
            <Card className="pa-4 mt-2 f-pa-4-xs">
                {/* Tag Description */}
                <DescriptionLabel>{t('About')}</DescriptionLabel>

                <Paragraph
                    style={{ fontSize: '14px', lineHeight: '20px', padding: '5px 0px' }}
                    editable={canUpdateTagDescription ? { onChange: handleSaveDescription } : false}
                    ellipsis={{ rows: 2, expandable: true, symbol: 'Read more' }}
                >
                    {updatedDescription || <EmptyValue />}
                </Paragraph>
                <Divider />
                {/* Tag Charts, Datasets and Owners */}
                <DetailsLayout>
                    <StatsBox>
                        <StatsLabel>{t('Applied to')}</StatsLabel>
                        {facetLoading && (
                            <div>
                                <EmptyStatsText>{t('Loading...')}</EmptyStatsText>
                            </div>
                        )}
                        {!facetLoading && aggregations && aggregations?.length === 0 && (
                            <div>
                                <EmptyStatsText>{t('No entities')}</EmptyStatsText>
                            </div>
                        )}
                        {!facetLoading &&
                            aggregations &&
                            aggregations?.map((aggregation) => {
                                if (aggregation?.count === 0) {
                                    return null;
                                }
                                return (
                                    <div key={aggregation?.value}>
                                        <StatsButton
                                            onClick={() =>
                                                navigateToSearchUrl({
                                                    filters: [
                                                        ...entityFilters,
                                                        {
                                                            field: ENTITY_FILTER_NAME,
                                                            values: [aggregation.value],
                                                        },
                                                    ],
                                                    unionType: UnionType.AND,
                                                    history,
                                                })
                                            }
                                            type="link"
                                        >
                                            <span data-testid={`stats-${aggregation?.value}`}>
                                                {aggregation?.count}{' '}
                                                {entityRegistry.getCollectionName(aggregation?.value as EntityType)}{' '}
                                                &gt;
                                            </span>
                                        </StatsButton>
                                    </div>
                                );
                            })}
                    </StatsBox>
                    <OwnersSection $isFromDrawer={isFromDrawer}>
                        <StatsLabel>{t('Owners')}</StatsLabel>
                        <div>
                            <OwnersContainer>
                                {data?.tag?.ownership?.owners?.map((owner) => (
                                    <ExpandedOwner
                                        entityUrn={urn}
                                        owner={owner}
                                        refetch={refetch}
                                        hidePopOver
                                        readOnly={!canUpdateTagOwners}
                                    />
                                ))}
                            </OwnersContainer>
                            {ownersEmpty && (
                                <Typography.Paragraph type="secondary">
                                    {t(EMPTY_MESSAGES.owners.title)}. {t(EMPTY_MESSAGES.owners.description)}
                                </Typography.Paragraph>
                            )}
                            <Tooltip
                                title={!canUpdateTagOwners ? t('UNAUTHORISED_TOOLTIP') : undefined}
                                overlayClassName="f-tooltip-white"
                            >
                                <Button
                                    type={ownersEmpty ? 'default' : 'text'}
                                    onClick={() => setShowAddModal(true)}
                                    aria-label={t('Add Owners')}
                                    disabled={!canUpdateTagOwners}
                                >
                                    <PlusOutlined aria-hidden="true" />
                                    {ownersEmpty ? (
                                        <OwnerButtonEmptyTitle>{t('Add Owners')}</OwnerButtonEmptyTitle>
                                    ) : (
                                        <OwnerButtonTitle>{t('Add Owners')}</OwnerButtonTitle>
                                    )}
                                </Button>
                            </Tooltip>
                        </div>
                        <div>
                            {showAddModal && (
                                <EditOwnersModal
                                    hideOwnerType
                                    refetch={refetch}
                                    onCloseModal={() => {
                                        setShowAddModal(false);
                                    }}
                                    urns={[urn]}
                                    entityType={EntityType.Tag}
                                />
                            )}
                        </div>
                    </OwnersSection>
                </DetailsLayout>
            </Card>
        </>
    );
}
